// State persistence utility for managing filter state across tabs and page refreshes

const STORAGE_KEY = 'dateFilter';
const TAB_ID_KEY = 'currentTabId';

// Generate unique tab ID
const generateTabId = () => {
  return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create tab ID
export const getTabId = () => {
  let tabId = localStorage.getItem(TAB_ID_KEY);
  if (!tabId) {
    tabId = generateTabId();
    localStorage.setItem(TAB_ID_KEY, tabId);
  }
  return tabId;
};

// Save filter state to localStorage
export const saveFilterState = (filterState) => {
  try {
    const stateToSave = {
      ...filterState,
      lastUpdated: Date.now(),
      tabId: getTabId()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
    return true;
  } catch (error) {
    console.warn('Failed to save filter state:', error);
    return false;
  }
};

// Load filter state from localStorage
export const loadFilterState = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (!serializedState) {
      return null;
    }
    
    const state = JSON.parse(serializedState);
    
    // Check if state is too old (older than 24 hours)
    const isExpired = state.lastUpdated && (Date.now() - state.lastUpdated) > 24 * 60 * 60 * 1000;
    if (isExpired) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    
    return state;
  } catch (error) {
    console.warn('Failed to load filter state:', error);
    return null;
  }
};

// Clear stored state
export const clearFilterState = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TAB_ID_KEY);
    return true;
  } catch (error) {
    console.warn('Failed to clear filter state:', error);
    return false;
  }
};

// Check if state is from current tab
export const isStateFromCurrentTab = (state) => {
  return state && state.tabId === getTabId();
};

// Validate filter state structure
export const validateFilterState = (state) => {
  if (!state || typeof state !== 'object') {
    return false;
  }

  // Check required fields
  const requiredFields = ['marketplace', 'startDate', 'endDate'];
  for (const field of requiredFields) {
    if (!state[field]) {
      return false;
    }
  }

  // Validate marketplace array
  if (!Array.isArray(state.marketplace) || state.marketplace.length === 0) {
    return false;
  }

  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
  if (!dateRegex.test(state.startDate) || !dateRegex.test(state.endDate)) {
    return false;
  }

  return true;
};
